import React, { useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import AppProductProp from '../../propTypes/appProductProp';
import {
  compose,
  displayMoney,
  IN_APP_PRODUCT_TYPE,
  IN_APP_PRODUCT_TYPES,
} from '../../../../services/utilityFunctions';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../../propTypes/StripeCardProp';
import PrintModal from './PrintModal';
import CertificateProp from '../../../academy/propTypes/certificateProp';
import MetaContext from '../../../../contextProviders/MetaContext';
import ShareModal from '../../../academy/modal/ShareModal';
import translate from '../../../../commonComponents/translate';
import EnrollModal from '../../../academy/EnrollModal';
import Request from '../../../../services/request';
import Notifications from '../../../../services/notifications';
import withCSRF from '../../../../commonComponents/withCSRF';
import IsAcademyPublic from '../../../../commonComponents/IsAcademyPublic';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import Loader from '../../../../services/loader';

function Print(props) {
  const {
    t,
    _csrf,
    certificate,
    appProducts,
    productType,
    countries,
    cards,
    onCardsChange,
    hasAcademyHeader,
  } = props;

  const isPoster = productType === IN_APP_PRODUCT_TYPE.POSTER;
  const isCertificate = productType === IN_APP_PRODUCT_TYPE.CERTIFICATE;

  const priceForProduct = type => {
    const product = appProducts.find(p => p.productType === type);
    if (!product || !product.userCurrency) {
      return null;
    }
    return displayMoney(
      product.userCurrency.total,
      product.userCurrency.symbol,
    );
  };

  const isProductFree = type => {
    const product = appProducts.find(p => p.productType === type);
    if (!product || !product.userCurrency) {
      return null;
    }
    return product.userCurrency.total === 0;
  };

  const productCurrency = type => {
    const product = appProducts.find(p => p.productType === type);
    if (!product || !product.userCurrency) {
      return null;
    }
    return product.userCurrency.code;
  };

  const loader = useRef(new Loader('#certificateLayout'));

  const { meta } = useContext(MetaContext);
  const { loggedUser } = useContext(LoggedUserContext);

  const [isLoading, setLoading] = useState(false);
  const [columnSize, setColumnSize] = useState(12);
  useEffect(
    () => {
      let certificateButtons = priceForProduct(
        IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE,
      )
        ? 2
        : 1;
      if (hasAcademyHeader) {
        certificateButtons += 1;
      }
      const numberOfButtons = isPoster ? 1 : certificateButtons;
      setColumnSize(12 / numberOfButtons);
    },
    [productType],
  );

  useEffect(
    () => {
      if (loader.current) {
        if (isLoading) {
          loader.current.show();
        } else {
          loader.current.hide();
        }
      }
    },
    [isLoading],
  );

  const [modalInfoShare, setModalInfoShare] = useState({ show: false });
  const showModalShare = () => setModalInfoShare({ show: true });
  const hideModalShare = () => setModalInfoShare({ show: false });

  const [modalInfoPrint, setModalInfoPrint] = useState({ show: false });
  const showModalPrint = () => setModalInfoPrint({ show: true });
  const hideModalPrint = () => setModalInfoPrint({ show: false });

  const [modalInfoBuy, setModalInfoBuy] = useState({ show: false });
  const showModalBuy = () => setModalInfoBuy({ show: true });
  const hideModalBuy = () => setModalInfoBuy({ show: false });

  const appProduct = appProducts.find(p => p.productType === productType);

  const certificateImage =
    certificate && certificate.image ? certificate.image.url : '';

  const appName = meta && meta.appName ? meta.appName : 'Bravon';

  const { protocol, host } =
    typeof window !== 'undefined' ? window.location : {};
  const fallBackShareUrl = `${protocol}//${host}/certificate/${
    certificate ? certificate.code : ''
  }`;

  const requestFreeCertificate = () => {
    setLoading(true);
    const params = {
      owner: loggedUser._id,
      amount: 0,
      currency: productCurrency(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE),
    };

    new Request(_csrf)
      .post(`/certificate/${certificate._id}/email`, params)
      .done(({ data }) => {
        setLoading(false);
        Notifications.showNotificationSuccess(t('success'), t('certificate_purchased'));
        window.open(data.link, '_blank');
      })
      .fail(err => {
        setLoading(false);
        Notifications.showErrorFromRequest(err, 'error_certificatePurchase');
      });
  };

  return (
    <div id="certificateLayout">
      {modalInfoShare.show && (
        <ShareModal
          url={certificate.share || fallBackShareUrl}
          onClose={hideModalShare}
        />
      )}
      {modalInfoBuy.show && (
        <EnrollModal
          itemType={IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE}
          itemId={certificate._id}
          onClose={hideModalBuy}
        />
      )}
      {modalInfoPrint.show && (
        <PrintModal
          certificateId={certificate ? certificate._id : null}
          cards={cards}
          countries={countries}
          appProduct={appProduct}
          onClose={hideModalPrint}
          onCardsChange={onCardsChange}
        />
      )}
      {isPoster && (
        <div className="certificate-bottom" style={{ paddingTop: '1rem' }}>
          <div className="row">
            {priceForProduct(productType) && (
              <div className={`col-lg-${columnSize}`}>
                <h5>
                  {t(
                    isPoster
                      ? 'certificate_posterByLetter'
                      : 'certificate_byLetter',
                    priceForProduct(productType),
                  )}
                </h5>
                <button
                  type="button"
                  onClick={showModalPrint}
                  className="btn m-btn--pill m-btn--wide btn-dynamic print-button"
                >
                  {t('certificate_orderNow')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="print-preview-content">
        <div className="watermark">
          {[1, 2, 3, 4].map(i => (
            <p key={i}>
              {[1, 2, 3, 4].map(() => `${appName.toUpperCase()}    `)}
            </p>
          ))}
        </div>
        {isPoster && (
          <div className="poster-preview-wrapper">
            <iframe
              title="Print Preview"
              className="poster-preview"
              src="/profile/poster/me"
            />
          </div>
        )}
        {isCertificate && (
          <div className="certificate-preview-wrapper">
            <img
              alt="Print Preview"
              className="certificate-preview"
              src={certificateImage}
            />
          </div>
        )}
      </div>
      <div className="certificate-bottom">
        <div className="row">
          {isCertificate && (
            <>
              {priceForProduct(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE) && (
                <>
                  <div className={`col-lg-${columnSize}`}>
                    <h5>
                      {t(
                        isPoster
                          ? 'certificate_posterByLetter'
                          : 'certificate_byLetter',
                        priceForProduct(productType),
                      )}
                    </h5>
                    <button
                      type="button"
                      onClick={showModalPrint}
                      className="btn m-btn--pill m-btn--wide btn-dynamic print-button"
                    >
                      {t('certificate_orderNow')}
                    </button>
                  </div>
                  <div className={`col-lg-${columnSize}`}>
                    <h5>
                      {
                        isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE)
                          ? t('certificate_byEmail_free')
                          : t(
                          'certificate_byEmail',
                          priceForProduct(
                            IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE,
                          ))
                      }
                    </h5>
                    <button
                      type="button"
                      className="btn m-btn--pill m-btn--wide btn-dynamic print-button"
                      onClick={isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE) ? requestFreeCertificate : showModalBuy}
                    >
                      {
                        t(isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE)
                          ? 'certificate_download'
                          : 'certificate_orderNow')
                      }
                    </button>
                  </div>
                </>
              )}
              {hasAcademyHeader && (
                <div className={`col-lg-${columnSize}`}>
                  <h5>{t('certificate_shareOnSocial')}</h5>
                  <button
                    onClick={showModalShare}
                    type="button"
                    className="btn m-btn--pill m-btn--wide btn-outline-dynamic print-button"
                  >
                    {t('certificate_share')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

Print.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  certificate: PropTypes.shape(CertificateProp.propType),
  appProducts: PropTypes.arrayOf(PropTypes.shape(AppProductProp.propType))
    .isRequired,
  productType: PropTypes.oneOf(IN_APP_PRODUCT_TYPES).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  onCardsChange: PropTypes.func,
  hasAcademyHeader: PropTypes.bool.isRequired,
};

Print.defaultProps = {
  certificate: null,
  onCardsChange: null,
};

export default compose(
  translate,
  withCSRF,
  IsAcademyPublic,
)(Print);
